import { BiddingLifecycleAction } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { useTransitionBiddingIntradayOrderState } from '~/hooks/bidding/useTransitionBiddingIntradayOrderState'
import { useErrorToast } from '~/hooks/useErrorToast'
import type { IntradayWithdrawConfirm } from '~/pages/IntradayBidding/hooks/useIntradayWithdrawConfirm'
import { useNotifyWithdrawAccepted } from '~/pages/IntradayBidding/hooks/useIntradayWithdrawNotice'
import { composeIntradayTableStatus } from '~/pages/IntradayBidding/models/intradayTableStatus'
import {
  formatWithdrawSummary,
  withdrawSummaryParams,
} from '~/pages/IntradayBidding/models/withdrawSummary'

type Props = {
  /** 開閉 ([[useIntradayWithdrawConfirm]])。 */
  confirm: IntradayWithdrawConfirm
}

/**
 * 行の取消ボタン (`IntradayBidRowActions`) から開く取消確認 modal。 入札一覧
 * (`BidCreateTab`) と板情報の自社入札 (`MarketBoardTab`) が同じものを開く (#10110)。
 *
 * 対象入札の概要 (コマ / エリア / 売買 / 価格 × MW) を提示し、 確定で WITHDRAW 状態遷移を
 * 呼ぶ。 hook が intradayList を invalidate するため成功後は一覧が自動更新される。
 * 予約中の札 (まだ JEPX の板に並んでいない) は板取消 (AC-BIN-008) とは別経路の予約取消
 * (AC-BIN-093) なので、 予約取消の文言に切り替える。 予約取消は行の取消ボタンだけで行い、
 * 詳細モーダルのフッターには出さない (機能詳細 6)。 action はどちらも WITHDRAW
 * (予約取消 / 板取消の種別は backend が送信軸から判定する)。
 */
export const IntradayWithdrawConfirmDialog = ({
  confirm,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const notifyWithdrawAccepted = useNotifyWithdrawAccepted()
  const showErrorToast = useErrorToast()
  // 提出など同じ画面の他の状態遷移とは別インスタンスにして loading 状態を分離する。
  const { mutateAsync: withdrawAsync, isPending: isWithdrawing } =
    useTransitionBiddingIntradayOrderState()

  const { target } = confirm
  // 判定は一覧のステータス列と同じ合成 (詳細モーダルの isReserved と同じ)。
  const isReserved =
    target !== undefined && composeIntradayTableStatus(target) === 'reserved'
  const textKey = isReserved
    ? 'page:biddingIntraday.reservationCancelConfirm'
    : 'page:biddingIntraday.withdrawConfirm'

  const handleConfirm = async (): Promise<void> => {
    if (target === undefined) return
    // 送信開始時点の対象 id を捕捉する (closeIfStill の判定で使う)。
    const targetId = target.id
    try {
      const response = await withdrawAsync({
        id: targetId,
        action: BiddingLifecycleAction.WITHDRAW,
        expectedUpdatedAt: target.updatedAt,
      })
      // AC-BIN-008b: JEPX 取消がリトライ中なら処理中トースト、 成立なら成功トースト。
      notifyWithdrawAccepted(targetId, response.order, t(`${textKey}.success`))
      confirm.closeIfStill(targetId)
    } catch {
      showErrorToast({
        title: t('page:biddingIntraday.withdrawConfirm.error'),
      })
    }
  }

  return (
    <ConfirmDialog
      body={
        target === undefined ? null : isReserved ? (
          t(
            `${textKey}.body`,
            // AC-BIN-043 でも入札量は単発スナップショット値 = MW。 行の保持値 (volumeMw) が MW なので
            // 換算せずに渡す。
            withdrawSummaryParams(t, {
              timeCode: target.timeCode,
              areaCode: target.areaCode,
              direction: target.direction,
              priceYenPerKwh: target.priceYenPerKwh,
              volumeMw: target.volumeMw,
            }),
          )
        ) : (
          // AC-BIN-008: 板取消は本文の次の行に札の概要を出す (#11458)。
          <>
            <p>{t('page:biddingIntraday.withdrawConfirm.body')}</p>
            <p>
              {formatWithdrawSummary(t, {
                timeCode: target.timeCode,
                areaCode: target.areaCode,
                direction: target.direction,
                orderMethod: target.orderMethod,
                priceYenPerKwh: target.priceYenPerKwh,
                // 行の volumeMw は MW 保持 (AC-BIN-043)。 概要も MW で出すので換算しない。
                volumeMw: target.volumeMw,
              })}
            </p>
          </>
        )
      }
      confirmLabel={t(`${textKey}.confirm`)}
      destructive
      isLoading={isWithdrawing}
      isOpen={target !== undefined}
      onCancel={confirm.close}
      onConfirm={() => {
        void handleConfirm()
      }}
      title={t(`${textKey}.title`)}
    />
  )
}
